import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PROGRAMMATIC_JUMP_EVENT } from "../utils/scroll";
export const REVEAL_EASE = [0.22, 1, 0.36, 1] as const;
/** Per-child stagger; delays are capped so at most 4 children stagger. */
export const STAGGER = 0.05;
const MAX_DELAY = STAGGER * 3;
const PRELOAD_MARGIN = 200;
type RevealProps = {
  children: React.ReactNode;
  className?: string;
  delay?: number;
};
type RevealState = "pending" | "instant" | "animate";

/**
 * Subtle scroll reveal that never hides content: starts at 35% opacity, begins
 * 200px before entering the viewport, and shows instantly if the element is
 * already on/above screen or was skipped by a programmatic jump.
 */
export function Reveal({
  children,
  className,
  delay = 0
}: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const [state, setState] = useState<RevealState>("pending");
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (reduce || el.getBoundingClientRect().top < window.innerHeight + PRELOAD_MARGIN) {
      setState("instant");
    }
  }, [reduce]);
  useEffect(() => {
    if (state !== "pending") return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      if (entry.boundingClientRect.bottom < 0) {
        setState("instant");
        observer.disconnect();
      } else if (entry.isIntersecting) {
        setState("animate");
        observer.disconnect();
      }
    }, {
      threshold: 0.01,
      rootMargin: `0px 0px ${PRELOAD_MARGIN}px 0px`
    });
    observer.observe(el);
    const onJump = () => {
      setState("instant");
      observer.disconnect();
    };
    window.addEventListener(PROGRAMMATIC_JUMP_EVENT, onJump);
    return () => {
      observer.disconnect();
      window.removeEventListener(PROGRAMMATIC_JUMP_EVENT, onJump);
    };
  }, [state]);
  return <motion.div ref={ref} className={className} initial={false} animate={state === "pending" ? {
    opacity: 0.35,
    y: 12
  } : {
    opacity: 1,
    y: 0
  }} transition={state === "instant" ? {
    duration: 0
  } : {
    duration: 0.25,
    ease: REVEAL_EASE,
    delay: Math.min(delay, MAX_DELAY)
  }} data-id="element-352" data-mp="vuoxdqgrh9u2">
      {children}
    </motion.div>;
}